import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import apiService from '../services/api';
import classNames from 'classnames';
import { format } from 'date-fns';
import {
  LineChart,
  Line,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface TestScenario {
  id: string;
  name: string;
  description: string;
  startDate: Date;
  endDate: Date;
  sourceIds: number[];
  expectedPatterns: string[];
  expectedRiskScores?: {
    military?: number;
    diplomatic?: number;
    economic?: number;
    informationWarfare?: number;
    cyber?: number;
    energy?: number;
    overall?: number;
  };
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
  isActive: boolean;
}

interface TestResult {
  articleId: string;
  analysis: any;
  expectedScores: any;
  accuracy: {
    military: number;
    diplomatic: number;
    economic: number;
    informationWarfare: number;
    cyber: number;
    energy: number;
    overall: number;
  };
  timestamp: Date;
}

interface WeightConfiguration {
  military: number;
  diplomatic: number;
  economic: number;
  informationWarfare: number;
  cyber: number;
  energy: number;
}

export const AdminTesting: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<'historical' | 'scenarios' | 'prompts' | 'weights'>('historical');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Historical testing state
  const [dateRange, setDateRange] = useState({
    startDate: format(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
  });
  const [historicalArticles, setHistoricalArticles] = useState<any[]>([]);
  const [historicalResults, setHistoricalResults] = useState<TestResult[]>([]);

  // Scenarios state
  const [scenarios, setScenarios] = useState<TestScenario[]>([]);
  const [selectedScenario, setSelectedScenario] = useState<string | null>(null);
  const [scenarioResults, setScenarioResults] = useState<TestResult[]>([]);
  const [showAddScenario, setShowAddScenario] = useState(false);
  const [editingScenario, setEditingScenario] = useState<TestScenario | null>(null);

  // Prompts state
  const [prompts, setPrompts] = useState({
    tier1: '',
    tier2: '',
    tier3: '',
  });
  const [promptResults, setPromptResults] = useState<any>({});
  const [testArticle, setTestArticle] = useState({
    title: '',
    content: '',
    url: 'https://test.example.com',
    publishedAt: new Date().toISOString().slice(0, 16), // For datetime-local input
  });

  // Weights state
  const [weights, setWeights] = useState<WeightConfiguration>({
    military: 0.25,
    diplomatic: 0.15,
    economic: 0.20,
    informationWarfare: 0.15,
    cyber: 0.15,
    energy: 0.10,
  });
  const [weightTestResults, setWeightTestResults] = useState<any>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchScenarios();
    fetchCurrentWeights();
  }, [isAuthenticated]);

  const fetchScenarios = async () => {
    try {
      const response = await apiService.getTestScenarios();
      setScenarios(response.scenarios);
    } catch (error) {
      console.error('Failed to fetch scenarios:', error);
    }
  };

  const fetchCurrentWeights = async () => {
    try {
      const response = await apiService.getCurrentWeights();
      setWeights(response.weights);
    } catch (error) {
      console.error('Failed to fetch weights:', error);
    }
  };

  const handleFetchHistoricalArticles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getHistoricalArticles(
        dateRange.startDate,
        dateRange.endDate,
      );
      setHistoricalArticles(response.articles);
    } catch (error: any) {
      setError('Failed to fetch historical articles');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestHistoricalArticles = async (articleIds: number[]) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.testHistoricalArticles({
        articleIds,
        countryCode: 'EST',
        tier: 2,
      });
      setHistoricalResults(response.results);
    } catch (error: any) {
      setError('Failed to test historical articles');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestScenario = async (scenarioId?: string) => {
    const targetScenario = scenarioId || selectedScenario;
    if (!targetScenario) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.testScenario(targetScenario, {
        countryCode: 'EST',
        tier: 2,
      });
      setScenarioResults(response.results);
    } catch (error: any) {
      setError('Failed to test scenario');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestPrompts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.testPromptVariations({
        article: testArticle,
        countryCode: 'EST',
        prompts,
      });
      setPromptResults(response.results);
    } catch (error: any) {
      setError('Failed to test prompts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestWeights = async () => {
    setLoading(true);
    setError(null);
    try {
      const articleIds = historicalArticles.slice(0, 20).map(a => a.id);
      const response = await apiService.testWeightConfiguration({
        articleIds,
        weights,
      });
      setWeightTestResults(response);
    } catch (error: any) {
      setError('Failed to test weights');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateWeights = async () => {
    setLoading(true);
    setError(null);
    try {
      await apiService.updateRiskWeights(weights);
      alert('Weights updated successfully');
    } catch (error: any) {
      setError('Failed to update weights');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const renderAccuracyChart = (results: TestResult[]) => {
    if (!results.length) return null;

    const data = [
      { category: 'Military', accuracy: average(results.map(r => r.accuracy.military)) },
      { category: 'Diplomatic', accuracy: average(results.map(r => r.accuracy.diplomatic)) },
      { category: 'Economic', accuracy: average(results.map(r => r.accuracy.economic)) },
      { category: 'Info Warfare', accuracy: average(results.map(r => r.accuracy.informationWarfare)) },
      { category: 'Cyber', accuracy: average(results.map(r => r.accuracy.cyber)) },
      { category: 'Energy', accuracy: average(results.map(r => r.accuracy.energy)) },
    ];

    return (
      <ResponsiveContainer width="100%" height={300}>
        <RadarChart data={data}>
          <PolarGrid />
          <PolarAngleAxis dataKey="category" />
          <PolarRadiusAxis angle={90} domain={[0, 1]} />
          <Radar
            name="Accuracy"
            dataKey="accuracy"
            stroke="#3B82F6"
            fill="#3B82F6"
            fillOpacity={0.6}
          />
          <Tooltip formatter={(value: number) => `${(value * 100).toFixed(1)}%`} />
        </RadarChart>
      </ResponsiveContainer>
    );
  };

  const renderWeightComparison = () => {
    if (!weightTestResults) return null;

    const data = weightTestResults.originalScores.map((original: number, i: number) => ({
      index: i,
      original,
      adjusted: weightTestResults.adjustedScores[i],
    }));

    return (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="index" />
          <YAxis domain={[0, 10]} />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="original" stroke="#8B5CF6" name="Original Scores" />
          <Line type="monotone" dataKey="adjusted" stroke="#10B981" name="Adjusted Scores" />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const average = (numbers: number[]) => {
    return numbers.reduce((a, b) => a + b, 0) / numbers.length;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h2 className="text-3xl font-bold tc-text-primary mb-8">
        AI Testing & Fine-tuning
      </h2>

      {/* Tab Navigation */}
      <div className="tc-card mb-6">
        <div className="flex space-x-1 border-b border-gray-100">
          {(['historical', 'scenarios', 'prompts', 'weights'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={classNames(
                'px-4 py-3 font-medium text-sm transition-colors capitalize border-b-2',
                activeTab === tab
                  ? 'text-blue-600 border-blue-600 bg-blue-50'
                  : 'tc-text-secondary border-transparent hover:tc-text-primary hover:bg-gray-50'
              )}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-4 tc-card error-bg p-4">
          <p>{error}</p>
        </div>
      )}

      {/* Historical Testing Tab */}
      {activeTab === 'historical' && (
        <div className="space-y-6">
          <div className="tc-card tc-card-hover p-6">
            <h3 className="text-xl font-semibold mb-4">Historical Event Testing</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Start Date</label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md tc-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">End Date</label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md tc-input"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleFetchHistoricalArticles}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  Fetch Articles
                </button>
              </div>
            </div>

            {historicalArticles.length > 0 && (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  Found {historicalArticles.length} articles
                </p>
                <button
                  onClick={() => handleTestHistoricalArticles(historicalArticles.slice(0, 10).map(a => a.id))}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  Test First 10 Articles
                </button>
              </div>
            )}
          </div>

          {historicalResults.length > 0 && (
            <div className="tc-card tc-card-hover p-6">
              <h3 className="text-xl font-semibold mb-4">Analysis Accuracy</h3>
              {renderAccuracyChart(historicalResults)}
            </div>
          )}
        </div>
      )}

      {/* Scenarios Tab */}
      {activeTab === 'scenarios' && (
        <div className="space-y-6">
          <div className="tc-card tc-card-hover p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold tc-text-primary">Test Scenarios</h3>
              <button
                onClick={() => setShowAddScenario(true)}
                className="tc-btn tc-btn-primary"
              >
                Add New Scenario
              </button>
            </div>
            
            <div className="grid grid-cols-1 gap-4">
              {scenarios.map((scenario) => (
                <div
                  key={scenario.id}
                  className={classNames(
                    'p-4 border rounded-lg transition-colors',
                    selectedScenario === scenario.id
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-400'
                  )}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-semibold tc-text-primary">{scenario.name}</h4>
                      <p className="text-sm tc-text-secondary mt-1">
                        {scenario.description}
                      </p>
                      <div className="flex items-center space-x-4 mt-2 text-xs tc-text-tertiary">
                        <span>Date: {format(new Date(scenario.startDate), 'MMM dd')} - {format(new Date(scenario.endDate), 'MMM dd, yyyy')}</span>
                        <span>{scenario.sourceIds.length} sources</span>
                        <span>Created: {format(new Date(scenario.createdAt), 'MMM dd, yyyy')}</span>
                        {scenario.expectedPatterns && (
                          <span>Patterns: {scenario.expectedPatterns.join(', ')}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedScenario(selectedScenario === scenario.id ? null : scenario.id)}
                        className="tc-btn tc-btn-secondary text-sm"
                      >
                        {selectedScenario === scenario.id ? 'Hide Details' : 'View Details'}
                      </button>
                      <button
                        onClick={() => setEditingScenario(scenario)}
                        className="tc-btn tc-btn-secondary text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleTestScenario(scenario.id)}
                        disabled={loading}
                        className="tc-btn tc-btn-primary text-sm"
                      >
                        Test
                      </button>
                    </div>
                  </div>

                  {/* Scenario Details */}
                  {selectedScenario === scenario.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h5 className="font-medium tc-text-primary mb-3">Scenario Configuration</h5>
                      <div className="tc-bg-secondary p-3 rounded-lg space-y-2">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium">Date Range:</span>
                            <div className="text-xs tc-text-secondary">
                              {format(new Date(scenario.startDate), 'MMM dd, yyyy')} - {format(new Date(scenario.endDate), 'MMM dd, yyyy')}
                            </div>
                          </div>
                          <div>
                            <span className="font-medium">Sources:</span>
                            <div className="text-xs tc-text-secondary">
                              {scenario.sourceIds.length} news sources selected
                            </div>
                          </div>
                        </div>
                        <div>
                          <span className="font-medium">Expected Patterns:</span>
                          <div className="text-xs tc-text-secondary">
                            {scenario.expectedPatterns.join(', ')}
                          </div>
                        </div>
                        {scenario.expectedRiskScores && (
                          <div>
                            <span className="font-medium">Expected Risk Scores:</span>
                            <div className="text-xs tc-text-secondary">
                              {Object.entries(scenario.expectedRiskScores).map(([key, value]) => (
                                value !== undefined && <span key={key} className="ml-2">{key}: {value}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {scenarioResults.length > 0 && (
            <div className="tc-card tc-card-hover p-6">
              <h3 className="text-xl font-semibold mb-4">Scenario Results</h3>
              {renderAccuracyChart(scenarioResults)}
              
              <div className="mt-6 space-y-4">
                {scenarioResults.map((result, idx) => (
                  <div key={idx} className="border-t pt-4">
                    <h4 className="font-medium mb-2">Article {idx + 1}</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      {/* Display category scores dynamically */}
                      {result.analysis.categoryScores && 
                        Object.entries(result.analysis.categoryScores).map(([category, score]) => (
                          <div key={category}>
                            <span className="text-gray-600 dark:text-gray-400">
                              {category.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}:
                            </span>
                            <span className="ml-2 font-medium">
                              {String(score)}
                              {result.expectedScores?.[category.toLowerCase()] !== undefined && 
                                ` (expected: ${result.expectedScores[category.toLowerCase()]})`}
                            </span>
                          </div>
                        ))
                      }
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Overall:</span>
                        <span className="ml-2 font-medium">
                          {result.analysis.overallRiskScore}
                          {result.expectedScores.overall !== undefined && 
                            ` (expected: ${result.expectedScores.overall})`}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Accuracy:</span>
                        <span className="ml-2 font-medium">
                          {(result.accuracy.overall * 100).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Prompts Tab */}
      {activeTab === 'prompts' && (
        <div className="space-y-6">
          <div className="tc-card tc-card-hover p-6">
            <h3 className="text-xl font-semibold mb-4">Prompt Fine-tuning</h3>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-1">Test Article Title</label>
                <input
                  type="text"
                  value={testArticle.title}
                  onChange={(e) => setTestArticle({ ...testArticle, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md tc-input"
                  placeholder="Enter article title..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Test Article Content</label>
                <textarea
                  value={testArticle.content}
                  onChange={(e) => setTestArticle({ ...testArticle, content: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md tc-input"
                  rows={4}
                  placeholder="Enter article content..."
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Tier 1 Prompt (Haiku)</label>
                <textarea
                  value={prompts.tier1}
                  onChange={(e) => setPrompts({ ...prompts, tier1: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md tc-input font-mono text-sm"
                  rows={6}
                  placeholder="Enter custom Tier 1 prompt..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tier 2 Prompt (Sonnet)</label>
                <textarea
                  value={prompts.tier2}
                  onChange={(e) => setPrompts({ ...prompts, tier2: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md tc-input font-mono text-sm"
                  rows={6}
                  placeholder="Enter custom Tier 2 prompt..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tier 3 Prompt (Opus)</label>
                <textarea
                  value={prompts.tier3}
                  onChange={(e) => setPrompts({ ...prompts, tier3: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md tc-input font-mono text-sm"
                  rows={6}
                  placeholder="Enter custom Tier 3 prompt..."
                />
              </div>
            </div>

            <button
              onClick={handleTestPrompts}
              disabled={loading || !testArticle.title || !testArticle.content}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              Test Prompts
            </button>
          </div>

          {Object.keys(promptResults).length > 0 && (
            <div className="tc-card tc-card-hover p-6">
              <h3 className="text-xl font-semibold mb-4">Prompt Comparison Results</h3>
              <div className="space-y-4">
                {Object.entries(promptResults).map(([tier, result]: [string, any]) => (
                  <div key={tier} className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-2 capitalize">{tier}</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                      {result.analysis.categoryScores && 
                        Object.entries(result.analysis.categoryScores).map(([category, score]) => (
                          <div key={category}>
                            {category.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}: {String(score)}
                          </div>
                        ))
                      }
                      <div>Overall: {result.analysis.overallRiskScore}</div>
                    </div>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                      Tokens: {result.analysis.tokensUsed} | Cost: ${result.analysis.cost.toFixed(4)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Weights Tab */}
      {activeTab === 'weights' && (
        <div className="space-y-6">
          <div className="tc-card tc-card-hover p-6">
            <h3 className="text-xl font-semibold mb-4">Risk Weight Configuration</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(weights).map(([category, weight]) => (
                <div key={category}>
                  <label className="block text-sm font-medium mb-1 capitalize">
                    {category.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min="0"
                      max="0.5"
                      step="0.01"
                      value={weight}
                      onChange={(e) => setWeights({ ...weights, [category]: parseFloat(e.target.value) })}
                      className="flex-1"
                    />
                    <span className="w-12 text-right">{(weight * 100).toFixed(0)}%</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-700 rounded">
              <p className="text-sm">
                Total: {(Object.values(weights).reduce((a, b) => a + b, 0) * 100).toFixed(0)}%
                {Math.abs(Object.values(weights).reduce((a, b) => a + b, 0) - 1) > 0.01 && 
                  <span className="text-red-600 ml-2">(Must equal 100%)</span>
                }
              </p>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={handleTestWeights}
                disabled={loading || historicalArticles.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Test Weights
              </button>
              <button
                onClick={handleUpdateWeights}
                disabled={loading || Math.abs(Object.values(weights).reduce((a, b) => a + b, 0) - 1) > 0.01}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                Save Weights
              </button>
            </div>
          </div>

          {weightTestResults && (
            <div className="tc-card tc-card-hover p-6">
              <h3 className="text-xl font-semibold mb-4">Weight Impact Analysis</h3>
              {renderWeightComparison()}
              <div className="mt-4 p-4 bg-gray-100 dark:bg-gray-700 rounded">
                <p className="text-sm">
                  Correlation: {weightTestResults.correlation.toFixed(3)} 
                  <span className="ml-2 text-gray-600 dark:text-gray-400">
                    ({weightTestResults.correlation > 0.8 ? 'High' : weightTestResults.correlation > 0.5 ? 'Moderate' : 'Low'} correlation with original scores)
                  </span>
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Scenario Modal */}
      {showAddScenario && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="tc-card max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold tc-text-primary">Create New Test Scenario</h3>
                <button
                  onClick={() => setShowAddScenario(false)}
                  className="tc-text-secondary hover:tc-text-primary"
                >
                  ✕
                </button>
              </div>
              <ScenarioForm
                onSave={(scenario) => {
                  setScenarios([...scenarios, scenario]);
                  setShowAddScenario(false);
                }}
                onCancel={() => setShowAddScenario(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Scenario Modal */}
      {editingScenario && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="tc-card max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold tc-text-primary">Edit Test Scenario</h3>
                <button
                  onClick={() => setEditingScenario(null)}
                  className="tc-text-secondary hover:tc-text-primary"
                >
                  ✕
                </button>
              </div>
              <ScenarioForm
                scenario={editingScenario}
                onSave={(scenario) => {
                  setScenarios(scenarios.map(s => s.id === scenario.id ? scenario : s));
                  setEditingScenario(null);
                }}
                onCancel={() => setEditingScenario(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Scenario Form Component
interface ScenarioFormProps {
  scenario?: TestScenario;
  onSave: (scenario: TestScenario) => void;
  onCancel: () => void;
}

const ScenarioForm: React.FC<ScenarioFormProps> = ({ scenario, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: scenario?.name || '',
    description: scenario?.description || '',
    expectedPatterns: scenario?.expectedPatterns?.join(', ') || '',
    dateRange: {
      startDate: scenario ? format(new Date(scenario.startDate), 'yyyy-MM-dd') : format(new Date('2022-02-20'), 'yyyy-MM-dd'),
      endDate: scenario ? format(new Date(scenario.endDate), 'yyyy-MM-dd') : format(new Date('2022-02-25'), 'yyyy-MM-dd'),
    },
    sourceIds: scenario?.sourceIds || [] as number[],
    expectedRiskScores: scenario?.expectedRiskScores || {
      military: 0,
      diplomatic: 0,
      economic: 0,
      informationWarfare: 0,
      cyber: 0,
      energy: 0,
      overall: 0,
    },
    useRealArticles: true,
  });
  const [availableSources, setAvailableSources] = useState<any[]>([]);
  const [articles, setArticles] = useState<any[]>([]);
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [newArticle, setNewArticle] = useState({
    title: '',
    content: '',
    url: '',
    publishedAt: new Date(),
    expectedRiskScores: {
      military: 0,
      diplomatic: 0,
      economic: 0,
      informationWarfare: 0,
      cyber: 0,
      energy: 0,
      overall: 0,
    },
  });

  useEffect(() => {
    fetchAvailableSources();
  }, []);

  const fetchAvailableSources = async () => {
    try {
      const response = await apiService.getNewsSources();
      setAvailableSources(response);
    } catch (error) {
      console.error('Failed to fetch sources:', error);
    }
  };

  const loadUkraineInvasionTemplate = () => {
    setFormData({
      name: 'Ukraine Invasion 2022 - Enhanced',
      description: 'Comprehensive test scenario for the 2022 Russian invasion of Ukraine using real articles from multiple sources',
      expectedPatterns: 'military buildup, diplomatic escalation, hybrid warfare, information operations, NATO response',
      dateRange: {
        startDate: '2022-02-20',
        endDate: '2022-02-26',
      },
      sourceIds: availableSources
        .filter(source => 
          source.name.includes('Reuters') || 
          source.name.includes('BBC') || 
          source.name.includes('ERR') ||
          source.name.includes('Postimees') ||
          source.name.includes('ISW') ||
          source.name.includes('NATO')
        )
        .map(source => source.id),
      expectedRiskScores: {
        military: 3,
        diplomatic: 3,
        informationWarfare: 2,
        cyber: 1,
        economic: 2,
        energy: 1,
        overall: 9,
      },
      useRealArticles: true,
    });
  };

  const fetchRealArticles = async () => {
    if (!formData.useRealArticles) return;
    
    try {
      const response = await apiService.getHistoricalArticles(
        formData.dateRange.startDate,
        formData.dateRange.endDate,
        formData.sourceIds.length > 0 ? formData.sourceIds : undefined
      );
      
      const realArticles: any[] = response.articles.slice(0, 20).map((article: any) => ({
        title: article.title,
        content: article.content,
        url: article.url,
        publishedAt: new Date(article.publishedAt),
        expectedRiskScores: {
          military: 2,
          diplomatic: 2,
          economic: 1,
          informationWarfare: 1,
          cyber: 1,
          energy: 1,
          overall: 7,
        },
      }));
      
      setArticles(realArticles);
    } catch (error) {
      console.error('Failed to fetch real articles:', error);
    }
  };

  const addArticle = () => {
    setArticles([...articles, { ...newArticle }]);
    setNewArticle({
      title: '',
      content: '',
      url: 'https://example.com',
      publishedAt: new Date(),
      expectedRiskScores: {
        military: 0,
        diplomatic: 0,
        economic: 0,
        informationWarfare: 0,
        cyber: 0,
        energy: 0,
        overall: 0,
      },
    });
    setShowArticleForm(false);
  };

  const removeArticle = (index: number) => {
    setArticles(articles.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const scenarioData: TestScenario = {
      id: scenario?.id || formData.name.toLowerCase().replace(/\s+/g, '-'),
      name: formData.name,
      description: formData.description,
      startDate: new Date(formData.dateRange.startDate),
      endDate: new Date(formData.dateRange.endDate),
      sourceIds: formData.sourceIds,
      expectedPatterns: formData.expectedPatterns.split(',').map(p => p.trim()).filter(p => p),
      expectedRiskScores: formData.expectedRiskScores,
      createdAt: scenario?.createdAt || new Date(),
      updatedAt: new Date(),
      createdBy: scenario?.createdBy,
      isActive: scenario?.isActive ?? true,
    };
    
    onSave(scenarioData);
  };

  return (
    <div className="space-y-6">
      {/* Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium tc-text-primary mb-2">
            Scenario Name
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="tc-input w-full"
            placeholder="e.g., Ukraine Invasion 2022"
          />
        </div>
        <div>
          <label className="block text-sm font-medium tc-text-primary mb-2">
            Expected Patterns (comma-separated)
          </label>
          <input
            type="text"
            value={formData.expectedPatterns}
            onChange={(e) => setFormData({ ...formData, expectedPatterns: e.target.value })}
            className="tc-input w-full"
            placeholder="e.g., military buildup, diplomatic escalation"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium tc-text-primary mb-2">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="tc-input w-full h-20"
          placeholder="Describe what this scenario tests..."
        />
      </div>

      {/* Template Buttons */}
      <div className="flex space-x-4">
        <button
          onClick={loadUkraineInvasionTemplate}
          className="tc-btn tc-btn-secondary"
        >
          📋 Load Ukraine Invasion Template
        </button>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="useRealArticles"
            checked={formData.useRealArticles}
            onChange={(e) => setFormData({ ...formData, useRealArticles: e.target.checked })}
            className="mr-2"
          />
          <label htmlFor="useRealArticles" className="text-sm tc-text-primary">
            Use real articles from database
          </label>
        </div>
      </div>

      {/* Real Articles Configuration */}
      {formData.useRealArticles && (
        <div className="tc-bg-secondary p-4 rounded-lg">
          <h4 className="font-medium tc-text-primary mb-3">Real Articles Configuration</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium tc-text-secondary mb-1">Start Date</label>
              <input
                type="date"
                value={formData.dateRange.startDate}
                onChange={(e) => setFormData({
                  ...formData,
                  dateRange: { ...formData.dateRange, startDate: e.target.value }
                })}
                className="tc-input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium tc-text-secondary mb-1">End Date</label>
              <input
                type="date"
                value={formData.dateRange.endDate}
                onChange={(e) => setFormData({
                  ...formData,
                  dateRange: { ...formData.dateRange, endDate: e.target.value }
                })}
                className="tc-input w-full"
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium tc-text-secondary mb-1">News Sources</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded p-2">
              {availableSources.map(source => (
                <label key={source.id} className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={formData.sourceIds.includes(source.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          sourceIds: [...formData.sourceIds, source.id]
                        });
                      } else {
                        setFormData({
                          ...formData,
                          sourceIds: formData.sourceIds.filter(id => id !== source.id)
                        });
                      }
                    }}
                    className="mr-1"
                  />
                  {source.name}
                </label>
              ))}
            </div>
          </div>
          <button
            onClick={fetchRealArticles}
            className="tc-btn tc-btn-primary"
          >
            📥 Fetch Articles ({formData.sourceIds.length} sources selected)
          </button>
        </div>
      )}

      {/* Articles Section */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-medium tc-text-primary">Test Articles ({articles.length})</h4>
          {!formData.useRealArticles && (
            <button
              onClick={() => setShowArticleForm(true)}
              className="tc-btn tc-btn-secondary text-sm"
            >
              ➕ Add Manual Article
            </button>
          )}
        </div>

        {/* Article List */}
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {articles.map((article, index) => (
            <div key={index} className="tc-bg-secondary p-3 rounded flex justify-between items-start">
              <div className="flex-1">
                <h6 className="font-medium tc-text-primary text-sm">{article.title}</h6>
                <div className="text-xs tc-text-secondary mt-1">
                  {format(new Date(article.publishedAt), 'MMM dd, yyyy')} • {new URL(article.url).hostname}
                </div>
              </div>
              <button
                onClick={() => removeArticle(index)}
                className="tc-text-secondary hover:tc-text-primary ml-2"
              >
                🗑️
              </button>
            </div>
          ))}
        </div>

        {/* Add Article Form */}
        {showArticleForm && (
          <div className="tc-card p-4 mt-4">
            <h5 className="font-medium tc-text-primary mb-3">Add Test Article</h5>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Article title"
                value={newArticle.title}
                onChange={(e) => setNewArticle({ ...newArticle, title: e.target.value })}
                className="tc-input w-full"
              />
              <textarea
                placeholder="Article content"
                value={newArticle.content}
                onChange={(e) => setNewArticle({ ...newArticle, content: e.target.value })}
                className="tc-input w-full h-24"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="url"
                  placeholder="URL"
                  value={newArticle.url}
                  onChange={(e) => setNewArticle({ ...newArticle, url: e.target.value })}
                  className="tc-input"
                />
                <input
                  type="datetime-local"
                  value={format(new Date(newArticle.publishedAt), "yyyy-MM-dd'T'HH:mm")}
                  onChange={(e) => setNewArticle({ ...newArticle, publishedAt: new Date(e.target.value) })}
                  className="tc-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium tc-text-secondary mb-2">Expected Risk Scores (0-3)</label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(newArticle.expectedRiskScores || {}).map(([key, value]) => (
                    <div key={key}>
                      <label className="block text-xs tc-text-tertiary capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</label>
                      <input
                        type="number"
                        min="0"
                        max="3"
                        step="0.1"
                        value={value}
                        onChange={(e) => setNewArticle({
                          ...newArticle,
                          expectedRiskScores: {
                            ...newArticle.expectedRiskScores,
                            [key]: parseFloat(e.target.value)
                          }
                        })}
                        className="tc-input w-full text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex space-x-2">
                <button onClick={addArticle} className="tc-btn tc-btn-primary text-sm">Add Article</button>
                <button onClick={() => setShowArticleForm(false)} className="tc-btn tc-btn-secondary text-sm">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
        <button onClick={onCancel} className="tc-btn tc-btn-secondary">
          Cancel
        </button>
        <button 
          onClick={handleSave} 
          disabled={!formData.name || articles.length === 0}
          className="tc-btn tc-btn-primary"
        >
          {scenario ? 'Update' : 'Create'} Scenario
        </button>
      </div>
    </div>
  );
};